import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

/**
 * An orthographic diorama viewer tuned for old, low-end Android phones.
 *
 * Two decisions carry most of the performance:
 *
 * 1. The scene renders into a small fixed buffer that CSS stretches to fill the
 *    screen. Fragment work is the bottleneck on these GPUs, and this cuts it by
 *    an order of magnitude. It also *is* the art style, so it costs nothing.
 * 2. Rendering is on demand and capped. An idle diorama draws zero frames; a
 *    drag draws at most 30 per second.
 */
export interface Viewer {
  scene: THREE.Scene;
  camera: THREE.OrthographicCamera;
  controls: OrbitControls;
  /** Request one more frame. Cheap, safe to call repeatedly. */
  invalidate(): void;
  /**
   * Run a continuous loop instead of drawing on demand.
   *
   * Reason-counted: several things animate independently (two train loops, the
   * rotation snap) and a single boolean would let whichever finished first
   * switch the loop off underneath the others. The loop runs while any reason
   * is outstanding.
   */
  setAnimating(on: boolean, reason?: string): void;
  /**
   * Whether the continuous loop is running. The whole idle-cost argument rests
   * on this being false whenever nothing is moving, so it is worth being able to
   * assert on directly rather than inferring it from frame counts.
   */
  isAnimating(): boolean;
  /** Called with the seconds elapsed since the previous frame, before drawing. */
  onFrame(callback: (dt: number) => void): void;
  /**
   * Frame an object and remember the pose as "home". Returns the bounding
   * radius it fitted to, which callers need in order to place fog: ortho view
   * depth is measured from the camera, so fog distances only make sense
   * relative to how far back the camera ended up.
   */
  frame(object: THREE.Object3D): number;
  resetView(): void;
  /** Current camera orbit, angles in radians, plus the orthographic zoom. */
  getOrbit(): Orbit;
  /**
   * Move the camera. Elevation is clamped to the controls' polar limits, and
   * changing it refits the frustum, because how much vertical room the scene
   * needs depends on how steeply it is being looked at.
   */
  setOrbit(next: Partial<Orbit>): void;
  /** The elevation range the controls allow, in radians. */
  elevationRange(): { min: number; max: number };
  /** Last frame's cost, for the hidden debug panel. */
  stats(): { drawCalls: number; triangles: number; buffer: string };
  dispose(): void;
}

export interface Orbit {
  azimuth: number;
  elevation: number;
  zoom: number;
}

/** Longest edge of the render buffer, in pixels. The whole look lives here. */
const RENDER_LONG_EDGE = 360;

/** 30fps. Indistinguishable from 60 for a diorama, and half the work. */
const MIN_FRAME_MS = 1000 / 30;

/**
 * True isometric: 45 degrees round, and asin(1/sqrt(3)) - about 35.26 degrees -
 * up, which is the elevation at which the three world axes project to equal
 * lengths 120 degrees apart.
 *
 * It costs frame area. The room is 52 long and 7 tall, so a steeper view
 * projects more of that length onto the vertical axis, and the vertical is the
 * binding constraint in landscape. A flatter view fills the screen better; this
 * is the isometric look chosen over that.
 */
const DEFAULT_AZIMUTH = Math.PI / 4;
const DEFAULT_ELEVATION = Math.asin(1 / Math.sqrt(3));

function directionFrom(azimuth: number, elevation: number): THREE.Vector3 {
  return new THREE.Vector3(
    Math.cos(elevation) * Math.sin(azimuth),
    Math.sin(elevation),
    Math.cos(elevation) * Math.cos(azimuth),
  ).normalize();
}

const ISO_DIR = directionFrom(DEFAULT_AZIMUTH, DEFAULT_ELEVATION);

/** Buffer size for a container, preserving its aspect so nothing distorts. */
function bufferSize(w: number, h: number): [number, number] {
  const aspect = w / Math.max(h, 1);
  return aspect >= 1
    ? [RENDER_LONG_EDGE, Math.max(1, Math.round(RENDER_LONG_EDGE / aspect))]
    : [Math.max(1, Math.round(RENDER_LONG_EDGE * aspect)), RENDER_LONG_EDGE];
}

export function createViewer(container: HTMLElement): Viewer {
  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    alpha: false,
    powerPreference: 'default',
    stencil: false,
    depth: true,
  });
  // Not setPixelRatio(dpr): the buffer is deliberately fixed and small, and
  // setSize would otherwise multiply it back up by the device ratio.
  renderer.setPixelRatio(1);
  renderer.setClearColor(0x0b0d12, 1);
  renderer.shadowMap.enabled = false;
  renderer.toneMapping = THREE.NoToneMapping;
  container.appendChild(renderer.domElement);

  let [bufW, bufH] = bufferSize(container.clientWidth, container.clientHeight);
  renderer.setSize(bufW, bufH, false); // false: CSS controls the display size

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b0d12);

  const camera = new THREE.OrthographicCamera(-10, 10, 10, -10, 0.1, 500);
  camera.position.copy(ISO_DIR).multiplyScalar(100);

  /** World units visible across the vertical axis. Ortho's "zoom level". */
  let viewSize = 20;

  function updateProjection() {
    const aspect = bufW / bufH;
    camera.left = (-viewSize * aspect) / 2;
    camera.right = (viewSize * aspect) / 2;
    camera.top = viewSize / 2;
    camera.bottom = -viewSize / 2;
    camera.updateProjectionMatrix();
  }
  updateProjection();

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.15; // settles quickly rather than coasting
  controls.rotateSpeed = 0.5; // a flick should not send it spinning
  controls.zoomSpeed = 0.8;
  controls.enablePan = false;
  // Azimuth is deliberately unconstrained - the whole diorama spins, and the
  // walls nearest the camera hide themselves (see scene/walls.ts).
  //
  // Polar is clamped to a fairly narrow band, and that is not only about keeping
  // the camera above the floor. The framing has to hold at every angle the user
  // can reach, and a steep look-down projects the room's whole 52-unit length
  // onto the vertical axis - so a wide band would force the default view to be
  // zoomed way out just to accommodate an angle nobody wants.
  controls.minPolarAngle = THREE.MathUtils.degToRad(45);
  controls.maxPolarAngle = THREE.MathUtils.degToRad(70);
  controls.minZoom = 0.4;
  controls.maxZoom = 4;

  let frameRequested = false;
  let disposed = false;
  let lastRender = 0;
  const animationReasons = new Set<string>();
  const frameCallbacks: ((dt: number) => void)[] = [];

  function request() {
    if (frameRequested || disposed) return;
    frameRequested = true;
    requestAnimationFrame(tick);
  }

  function tick(now: number) {
    frameRequested = false;
    if (disposed) return;

    // The frame cap. Re-arm without drawing rather than drawing early; at 60Hz
    // this costs one extra empty callback per rendered frame.
    if (now - lastRender < MIN_FRAME_MS) {
      request();
      return;
    }

    const dt = lastRender === 0 ? 0 : Math.min((now - lastRender) / 1000, 0.1);
    lastRender = now;

    // update() re-fires 'change' while inertia bleeds off, which re-arms the
    // next frame; the loop therefore stops on its own when motion settles.
    controls.update();
    // Deliberately after update(): the snap corrects the camera that damping just
    // moved. Running it first would let damping overwrite the correction.
    advanceSnap(dt);
    for (const callback of frameCallbacks) callback(dt);
    renderer.render(scene, camera);

    if (animationReasons.size > 0) request();
  }

  const invalidate = request;
  controls.addEventListener('change', invalidate);

  // --- rotation snapping --------------------------------------------------
  // Letting go anywhere leaves the diorama at an arbitrary angle, which looks
  // accidental. Easing to the nearest eighth-turn keeps it reading as a
  // deliberate isometric view while still allowing a free spin on the way.
  const SNAP_STEP = Math.PI / 4; // 45 degrees
  /** Time constant of the pull, in seconds. Smaller lands harder. */
  const SNAP_TAU = 0.09;
  const SNAP_EPSILON = 0.002; // radians; close enough to finish exactly

  const snap = { active: false, target: 0 };
  const spherical = new THREE.Spherical();
  const offset = new THREE.Vector3();

  function currentAzimuth(): number {
    offset.copy(camera.position).sub(controls.target);
    spherical.setFromVector3(offset);
    return spherical.theta;
  }

  /** Angle above the horizon, which is what the fit depends on. */
  function currentElevation(): number {
    offset.copy(camera.position).sub(controls.target);
    return Math.asin(offset.y / Math.max(offset.length(), 1e-6));
  }

  function applyAzimuth(theta: number) {
    offset.copy(camera.position).sub(controls.target);
    spherical.setFromVector3(offset);
    spherical.theta = theta;
    offset.setFromSpherical(spherical);
    camera.position.copy(controls.target).add(offset);
    camera.lookAt(controls.target);
  }

  function beginSnap() {
    const from = currentAzimuth();
    const target = Math.round(from / SNAP_STEP) * SNAP_STEP;
    if (Math.abs(target - from) < SNAP_EPSILON) return;
    snap.active = true;
    snap.target = target;
    setAnimating(true, 'snap');
  }

  /**
   * Pull the azimuth toward the target exponentially. This runs *after*
   * controls.update() each frame, so it composes with the leftover damping
   * rather than fighting it: inertia keeps nudging, this keeps correcting, and
   * as the inertia decays the correction wins and lands exactly on the step.
   *
   * The alternative - clearing the damping impulse at release and tweening from
   * there - applies that impulse in one go, which is a visible jump after a fast
   * flick, since up to a whole frame of finger travel can be sitting in it.
   */
  function advanceSnap(dt: number) {
    if (!snap.active) return;
    const from = currentAzimuth();
    const error = snap.target - from;
    if (Math.abs(error) < SNAP_EPSILON) {
      applyAzimuth(snap.target);
      snap.active = false;
      setAnimating(false, 'snap');
      return;
    }
    applyAzimuth(snap.target - error * Math.exp(-dt / SNAP_TAU));
  }

  // 'start' fires on touch down; abandon any in-flight snap so a new drag is
  // never fighting the previous one.
  controls.addEventListener('start', () => {
    if (snap.active) {
      snap.active = false;
      setAnimating(false, 'snap');
    }
  });
  controls.addEventListener('end', () => {
    beginSnap();
    // Tilting changes how much vertical room the scene needs, so refit - but
    // only for a real tilt, or every pinch and spin would nudge the zoom.
    const elevation = currentElevation();
    if (Math.abs(elevation - fittedElevation) > THREE.MathUtils.degToRad(1.5)) {
      refit(elevation);
    }
  });

  function setAnimating(on: boolean, reason = 'scene') {
    if (on) {
      animationReasons.add(reason);
      request();
    } else {
      animationReasons.delete(reason);
    }
  }

  function isAnimating() {
    return animationReasons.size > 0;
  }

  function onFrame(callback: (dt: number) => void) {
    frameCallbacks.push(callback);
  }

  // --- resize -------------------------------------------------------------
  let resizeTimer: number | undefined;
  let lastW = container.clientWidth;
  let lastH = container.clientHeight;

  function applyResize() {
    const w = container.clientWidth;
    const h = Math.max(container.clientHeight, 1);
    // Android fires a flurry of tiny resizes as the system bars animate.
    if (Math.abs(w - lastW) < 2 && Math.abs(h - lastH) < 2) return;
    lastW = w;
    lastH = h;
    [bufW, bufH] = bufferSize(w, h);
    renderer.setSize(bufW, bufH, false);
    if (framed) frame(framed);
    else updateProjection();
    invalidate();
  }

  const onResize = () => {
    globalThis.clearTimeout(resizeTimer);
    resizeTimer = globalThis.setTimeout(applyResize, 150);
  };
  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(container);

  // --- GL context loss ----------------------------------------------------
  const canvas = renderer.domElement;
  const onContextLost = (e: Event) => {
    e.preventDefault();
    frameRequested = false;
  };
  const onContextRestored = () => invalidate();
  canvas.addEventListener('webglcontextlost', onContextLost, false);
  canvas.addEventListener('webglcontextrestored', onContextRestored, false);

  // --- framing ------------------------------------------------------------
  let framed: THREE.Object3D | undefined;
  /** Elevation the current viewSize was computed for. */
  let fittedElevation = DEFAULT_ELEVATION;
  const home = { position: camera.position.clone(), target: controls.target.clone() };

  /**
   * The vertical world extent the frustum needs so the box stays fully on screen
   * at any azimuth, for one given elevation.
   *
   * Azimuth is swept because spinning is free and must never clip. Elevation is
   * a parameter rather than a worst case: covering the whole tilt range would
   * mean permanently reserving room for the steepest look-down, which costs a
   * third of the frame at the angle people actually look from. Tilting refits
   * instead - see the 'end' handler.
   *
   * The projection of the box's eight corners is measured directly rather than
   * estimated in closed form. A formula is shorter, but an earlier one let the
   * scene clip while rotating: the corner that pokes out is not always the one
   * the algebra assumed, and a numerical sweep cannot be wrong about which it is.
   * This runs once per reframe, so the cost is irrelevant.
   */
  function fitViewSize(
    box: THREE.Box3,
    center: THREE.Vector3,
    aspect: number,
    elevation: number,
  ): number {
    const corners: THREE.Vector3[] = [];
    for (const x of [box.min.x, box.max.x]) {
      for (const y of [box.min.y, box.max.y]) {
        for (const z of [box.min.z, box.max.z]) {
          corners.push(new THREE.Vector3(x, y, z).sub(center));
        }
      }
    }

    const dir = new THREE.Vector3();
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    const worldUp = new THREE.Vector3(0, 1, 0);
    let needed = 0;

    for (let deg = 0; deg < 360; deg += 5) {
      const azimuth = (deg * Math.PI) / 180;
      // Camera basis for this orientation, looking at the centre.
      dir.set(
        Math.cos(elevation) * Math.sin(azimuth),
        Math.sin(elevation),
        Math.cos(elevation) * Math.cos(azimuth),
      ).normalize();
      right.crossVectors(worldUp, dir).normalize();
      up.crossVectors(dir, right).normalize();

      let halfH = 0;
      let halfV = 0;
      for (const corner of corners) {
        halfH = Math.max(halfH, Math.abs(corner.dot(right)));
        halfV = Math.max(halfV, Math.abs(corner.dot(up)));
      }
      // Vertical extent required to satisfy both axes at this orientation.
      needed = Math.max(needed, halfV * 2, (halfH * 2) / aspect);
    }

    return needed * 1.04; // a little air around the diorama
  }

  /** Re-run the fit for a new elevation, keeping the camera where it is. */
  function refit(elevation: number) {
    if (!framed) return;
    const box = new THREE.Box3().setFromObject(framed);
    if (box.isEmpty()) return;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    viewSize = fitViewSize(box, sphere.center, bufW / bufH, elevation);
    fittedElevation = elevation;
    updateProjection();
    invalidate();
  }

  function frame(object: THREE.Object3D): number {
    framed = object;
    const box = new THREE.Box3().setFromObject(object);
    if (box.isEmpty()) return 0;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const radius = Math.max(sphere.radius, 0.001);
    const aspect = bufW / bufH;
    fittedElevation = DEFAULT_ELEVATION;
    viewSize = fitViewSize(box, sphere.center, aspect, fittedElevation);
    updateProjection();

    controls.target.copy(sphere.center);
    // Ortho scale does not depend on distance, so this is free to choose - but
    // it is not arbitrary. View depth (and therefore fog) is measured from the
    // camera, so parking it far away pushes the whole scene past any sensible
    // fog range. Two radii back keeps the scene at depths near 1-3 radii.
    camera.position.copy(sphere.center).addScaledVector(ISO_DIR, radius * 2);
    // The camera sits two radii out, so the scene occupies depths of one to
    // three radii. Bracket that tightly rather than generously: depth precision
    // is spread over near..far, and surfaces here sit fractions of a unit apart
    // (see DOOR_STANDOFF in scene/train.ts).
    camera.near = radius * 0.85;
    camera.far = radius * 3.15;
    camera.updateProjectionMatrix();
    controls.update();

    home.position.copy(camera.position);
    home.target.copy(controls.target);
    invalidate();
    return radius;
  }

  function resetView() {
    camera.position.copy(home.position);
    controls.target.copy(home.target);
    camera.zoom = 1;
    camera.updateProjectionMatrix();
    controls.update();
    invalidate();
  }

  function getOrbit(): Orbit {
    return {
      azimuth: currentAzimuth(),
      elevation: currentElevation(),
      zoom: camera.zoom,
    };
  }

  function elevationRange() {
    return {
      min: Math.PI / 2 - controls.maxPolarAngle,
      max: Math.PI / 2 - controls.minPolarAngle,
    };
  }

  function setOrbit(next: Partial<Orbit>) {
    const current = getOrbit();
    const range = elevationRange();
    const elevation = THREE.MathUtils.clamp(
      next.elevation ?? current.elevation,
      range.min,
      range.max,
    );
    const azimuth = next.azimuth ?? current.azimuth;

    offset.copy(camera.position).sub(controls.target);
    const distance = offset.length();
    camera.position.set(
      controls.target.x + distance * Math.cos(elevation) * Math.sin(azimuth),
      controls.target.y + distance * Math.sin(elevation),
      controls.target.z + distance * Math.cos(elevation) * Math.cos(azimuth),
    );
    camera.lookAt(controls.target);

    if (next.zoom !== undefined) {
      camera.zoom = THREE.MathUtils.clamp(next.zoom, controls.minZoom, controls.maxZoom);
      camera.updateProjectionMatrix();
    }

    if (Math.abs(elevation - fittedElevation) > 1e-4) refit(elevation);
    else invalidate();
  }

  function stats() {
    return {
      drawCalls: renderer.info.render.calls,
      triangles: renderer.info.render.triangles,
      buffer: `${bufW}x${bufH}`,
    };
  }

  function dispose() {
    disposed = true;
    globalThis.clearTimeout(resizeTimer);
    resizeObserver.disconnect();
    canvas.removeEventListener('webglcontextlost', onContextLost);
    canvas.removeEventListener('webglcontextrestored', onContextRestored);
    controls.dispose();
    renderer.dispose();
    canvas.remove();
  }

  invalidate();

  return {
    scene,
    camera,
    controls,
    invalidate,
    setAnimating,
    isAnimating,
    onFrame,
    frame,
    resetView,
    getOrbit,
    setOrbit,
    elevationRange,
    stats,
    dispose,
  };
}

/** True when the device can give us a WebGL context at all. */
export function hasWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
  } catch {
    return false;
  }
}
